import { observer } from 'mobx-react-lite'
import { StyleSheet, View } from 'react-native'
import { Player } from '@ppwr/poker'
import { useStore } from '../../models'
import { PlayerChips } from '../../components/ANewDesign/PlayerChips'
import { palette } from '../../theme/palette'
import { Card } from '../../components/ANewDesign/Card'
import { getLastActionText } from '../../components/ANewDesign/utils'
import { DS } from '../../design-system'
import { useThreeCardSize } from './useThreeCardSizes'

const MY_TURN_CONTAINER_WIDTH = 101
interface ThreeCardPlayerProps {
  isBot: boolean
  player?: Player
  right?: boolean
}

export const ThreeCardPlayer = observer(({ player, isBot, right }: ThreeCardPlayerProps) => {
  const {
    game: { communityCards, currentPlayerId, isPlayerInPlay, isGamePlaying, winners }
  } = useStore()
  const size = useThreeCardSize()
  const isSmall = size === 'small'
  const cardSize = isSmall ? 16 : 18
  const endOfTurn = winners.length !== 0
  const isWinner = player && winners.length && winners[0].playerName === player.name
  const isMyTurn = player && currentPlayerId && player.id === currentPlayerId

  const name = player ? (!isBot ? 'You' : player.name) : 'Loading...'

  const lastActionText = player ? getLastActionText(player) : undefined

  const applyOpacity = player && (!isPlayerInPlay(player) || (winners.length && !isWinner))

  const getStatusText = (isWinner: boolean, isMyTurn: boolean, isBot: boolean) => {
    if (isWinner) {
      return 'I WIN'
    }
    if (isMyTurn && !isBot) {
      return 'YOUR TURN'
    }
    if (isBot) {
      return 'MY TURN'
    }
    return ''
  }

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.handInfoContainer,
          isBot ? (right ? styles.handInfoRight : styles.handInfoLeft) : { left: '100%' }
        ]}
      >
        {isGamePlaying ? (
          <>
            <View style={{ height: isBot ? 26 : 28, alignItems: right ? 'flex-end' : 'flex-start' }}>
              {isWinner || (!endOfTurn && isMyTurn) ? (
                <View style={[styles.myTurnContainer, right ? styles.myTurnRight : styles.myTurnLeft]}>
                  <DS.Text variant="Caption/Bold" color="Global/White" numberOfLines={1}>
                    {getStatusText(!!isWinner, isMyTurn, isBot)}
                  </DS.Text>
                </View>
              ) : null}
            </View>
            <View style={{ height: 20, alignItems: right ? 'flex-end' : 'flex-start' }}>
              {lastActionText && !winners.length ? (
                <View
                  style={[
                    styles.lastActionContainer,
                    right ? styles.lastActionRight : styles.lastActionLeft,
                    { direction: 'ltr' }
                  ]}
                >
                  <DS.Text variant="Mini/Bold" style={styles.lastActionText}>
                    {lastActionText}
                  </DS.Text>
                </View>
              ) : null}
            </View>
          </>
        ) : null}
      </View>
      {isBot ? (
        <DS.Icon
          style={{ transform: !right ? [{ rotateY: '180deg' }] : [] }}
          name={right ? 'Person' : 'Person--filled'}
          color={'Global/Black'}
          size={isSmall ? 30 : 40}
        />
      ) : null}
      <DS.Text variant={isSmall ? 'Body Small/Medium' : 'Body Large/Medium'}>{name}</DS.Text>
      <PlayerChips variant="Caption/Bold" chips={player?.chips ?? 5000} color="Global/Black" chipsSize={16} />
      {isBot ? (
        <View style={[styles.botCardsContainer]}>
          <Card
            card={winners.length ? player?.cards[0] : undefined}
            fontSize={cardSize}
            rotateDegrees={'-6deg'}
            shadow
            disabled={!!applyOpacity}
          />
          <Card
            card={winners.length ? player?.cards[1] : undefined}
            fontSize={cardSize}
            rotateDegrees={winners.length && isWinner ? '0deg' : '6deg'}
            shadow
            disabled={!!applyOpacity}
          />
          {winners.length && isWinner ? (
            <Card card={communityCards[0]} fontSize={cardSize} rotateDegrees={'6deg'} shadow />
          ) : null}
        </View>
      ) : null}
    </View>
  )
})

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    // overflow: 'visible',
    width: 100
    // height: 300
  },
  playerInfoContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4
  },
  botCardsContainer: {
    flexDirection: 'row',
    zIndex: 100
  },
  handInfoContainer: {
    position: 'absolute',
    top: '-30%',
    width: '100%',
    gap: 2
  },
  handInfoLeft: {
    left: '80%'
  },
  handInfoRight: {
    right: '80%'
  },
  myTurnContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: MY_TURN_CONTAINER_WIDTH,
    backgroundColor: palette.orange,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderTopRightRadius: 16,
    borderTopLeftRadius: 16
  },
  myTurnLeft: {
    borderBottomRightRadius: 16
  },
  myTurnRight: {
    borderBottomLeftRadius: 16
  },
  lastActionContainer: {
    backgroundColor: palette.white,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    height: 20,
    borderColor: palette.lightGrey,
    borderBottomRightRadius: 16,
    borderBottomLeftRadius: 16
  },
  lastActionLeft: {
    borderTopRightRadius: 16
  },
  lastActionRight: {
    borderTopLeftRadius: 16
  },
  lastActionText: {
    textAlign: 'center'
  }
})
